import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import Axios from 'axios';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spin, message, Button } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import dayjs from 'dayjs';
import { EmptyState } from '../../common';
import './ArticleDetailPage.css';

// 記事本文ページ
// URL: /article/:seriesCode/:articleId
// - 単一記事 (GET /api/article/:id) + シリーズ全体 (GET /api/article/by-series/:seriesCode) を並行取得
// - 本文は react-markdown + remark-gfm でレンダリング
// - 左サイドバーに目次（h2/h3 自動抽出 + smooth scroll + スクロール連動ハイライト）
// - サムネイルヒーロー + サブタイトル + 著者プロフィール + 前/次の記事ナビゲーション
// - role 1/7 は非公開記事(is_public=false)で 403 → 閲覧不可メッセージを表示
function ArticleDetailPage(props) {
  const params = useParams();
  const reduxUser = useSelector((state) => state.user);
  // role は props.user 優先、無ければ redux から取得
  const userRole =
    (props.user && props.user.userData && props.user.userData.role) ||
    (reduxUser && reduxUser.userData && reduxUser.userData.role);

  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [series, setSeries] = useState(null);
  const [siblings, setSiblings] = useState([]); // /by-series で取得した配列（episode_number 付き）
  const [forbidden, setForbidden] = useState(false); // 403（非公開記事を閲覧権限なしで開いた）
  const [activeTocId, setActiveTocId] = useState(null); // スクロール連動の現在見出し

  // ─────────────────────────────────
  // データ取得（両 GET に ?role= を付与）
  // ─────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    const roleQuery = userRole !== undefined && userRole !== null ? `?role=${userRole}` : '';
    try {
      const [resArt, resSiblings] = await Promise.all([
        Axios.get(`/api/article/${params.articleId}${roleQuery}`).catch((err) => err.response || { __error: err }),
        Axios.get(`/api/article/by-series/${params.seriesCode}${roleQuery}`).catch((err) => err.response || { __error: err }),
      ]);

      // 記事本体の 403（非公開・権限なし）をハンドリング
      if (resArt && resArt.status === 403) {
        setForbidden(true);
      } else if (resArt && resArt.data && resArt.data.success) {
        setArticle(resArt.data.article);
      } else {
        message.error('記事の取得に失敗しました');
      }

      // シリーズ情報（前後ナビ用）。403 等は無視（本文側で判定済み）
      if (resSiblings && resSiblings.data && resSiblings.data.success) {
        setSeries(resSiblings.data.series);
        setSiblings(resSiblings.data.articles || []);
      }
    } catch (err) {
      console.log(err);
      message.error('記事の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [params.articleId, params.seriesCode, userRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─────────────────────────────────
  // 前後ナビゲーション + 自記事の episode_number を siblings から解決
  // ─────────────────────────────────
  const { prev, next, currentEpisode } = useMemo(() => {
    if (!article || siblings.length === 0) {
      return { prev: null, next: null, currentEpisode: null };
    }
    const idx = siblings.findIndex(
      (s) => String(s._id) === String(article._id)
    );
    if (idx === -1) {
      return { prev: null, next: null, currentEpisode: null };
    }
    return {
      prev: idx > 0 ? siblings[idx - 1] : null,
      next: idx < siblings.length - 1 ? siblings[idx + 1] : null,
      currentEpisode: siblings[idx].episode_number,
    };
  }, [article, siblings]);

  // ─────────────────────────────────
  // 目次を MD 本文から抽出（## と ### のみ）
  // 各見出しに位置index ベースの id を付与し、目次クリックで scrollIntoView
  // ─────────────────────────────────
  const tocEntries = useMemo(() => {
    if (!article || !article.content_md) return [];
    const entries = [];
    const lines = article.content_md.split(/\r?\n/);
    let inCodeBlock = false;
    lines.forEach((line) => {
      // コードブロック内の # は見出しとして扱わない
      if (/^```/.test(line)) {
        inCodeBlock = !inCodeBlock;
        return;
      }
      if (inCodeBlock) return;
      const m2 = /^##\s+(.+)$/.exec(line);
      const m3 = /^###\s+(.+)$/.exec(line);
      if (m2) {
        entries.push({ level: 2, text: m2[1].trim() });
      } else if (m3) {
        entries.push({ level: 3, text: m3[1].trim() });
      }
    });
    // id は (level, index) ベース
    let h2Idx = 0;
    let h3Idx = 0;
    return entries.map((e) => {
      const id = e.level === 2 ? `adp-h2-${h2Idx++}` : `adp-h3-${h3Idx++}`;
      return { ...e, id };
    });
  }, [article]);

  // 本文コンテナ ref（レンダリング後に見出しへ id を付与するため）
  const bodyRef = useRef(null);

  // react-markdown の components マッピング
  // ※ 見出しの id はレンダリング時のカウンタではなく、描画後に DOM 順で付与する
  //   （カウンタ方式は StrictMode の二重レンダリングや再レンダリングで id がずれ、
  //     目次クリックの scrollIntoView 対象が見つからなくなるため）
  const componentsMap = useMemo(
    () => ({
      // a タグは外部リンクなら別タブで開く
      a: ({ href, children }) => {
        const isExternal = href && /^https?:\/\//i.test(href);
        return (
          <a
            href={href}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
          >
            {children}
          </a>
        );
      },
    }),
    []
  );

  // 描画後、本文内の h2/h3 を DOM 順で走査し、tocEntries と同じ規則で id を付与
  // （tocEntries も level ごとの出現順 index で id を作るので DOM 順と一致する）
  useLayoutEffect(() => {
    if (!bodyRef.current) return;
    const headings = bodyRef.current.querySelectorAll('h2, h3');
    let h2Idx = 0;
    let h3Idx = 0;
    headings.forEach((el) => {
      const tag = el.tagName.toLowerCase();
      el.id = tag === 'h2' ? `adp-h2-${h2Idx++}` : `adp-h3-${h3Idx++}`;
    });
  }, [article, tocEntries]);

  // 目次クリック処理（smooth scroll + hash 更新）
  const onTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveTocId(id);
      // URL hash も更新（ブラウザ戻る対応）
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', `#${id}`);
      }
    }
  };

  // ─────────────────────────────────
  // スクロール連動で目次のアクティブ項目をハイライト
  // 見出し要素のうち、ビューポート上 1/3 を最後に越えたものを active にする
  // ─────────────────────────────────
  useEffect(() => {
    if (tocEntries.length === 0) return undefined;
    const handler = () => {
      const threshold = window.scrollY + window.innerHeight * 0.33;
      let current = tocEntries[0].id;
      for (let i = 0; i < tocEntries.length; i++) {
        const el = document.getElementById(tocEntries[i].id);
        if (el && el.offsetTop <= threshold) {
          current = tocEntries[i].id;
        }
      }
      setActiveTocId(current);
    };
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler);
    handler(); // 初期判定
    return () => {
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    };
  }, [tocEntries]);

  // ─────────────────────────────────
  // レンダリング
  // ─────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  // 403: 非公開記事を閲覧権限なしで開いた（role 1/7）
  if (forbidden) {
    return (
      <div style={{ padding: 40 }}>
        <EmptyState message="この記事は閲覧できません" />
      </div>
    );
  }

  if (!article) {
    return (
      <div style={{ padding: 40 }}>
        <EmptyState message="記事が見つかりません" />
      </div>
    );
  }

  // 著者情報（populate されている想定。文字列 ID ならフォールバック）
  const instructor =
    article.instructor_id && typeof article.instructor_id === 'object'
      ? article.instructor_id
      : null;
  const authorName = instructor
    ? instructor.name
    : article.instructor_email || '著者不明';
  // 肩書: title 優先（旧 sector フォールバック）/ プロフィール文: profile 優先（旧 description フォールバック）
  const authorTitle =
    (instructor && (instructor.title || instructor.sector)) || '';
  const authorBio =
    (instructor && (instructor.profile || instructor.description)) || '';
  const authorPhoto = instructor && instructor.photo_url;
  const authorInitial = (authorName || '?').slice(0, 1);

  // 公開日 YYYY.MM.DD 形式
  const publishDateStr = article.publish_date
    ? dayjs(article.publish_date).format('YYYY.MM.DD')
    : '';

  return (
    <div className="adp-root">
      <div className="adp-container">
        <nav className="adp-breadcrumb" aria-label="パンくず">
          <a href="/">Home</a>
          <span className="adp-bc-sep">›</span>
          <a href="/article">記事一覧</a>
          <span className="adp-bc-sep">›</span>
          {series ? (
            <a href={`/article/${series.series_code}`}>{series.series_name}</a>
          ) : (
            <span>シリーズ</span>
          )}
          <span className="adp-bc-sep">›</span>
          <span className="adp-bc-current" aria-current="page">
            {article.title}
          </span>
        </nav>

        {/* role 9 専用: 編集ボタン */}
        {userRole === 9 && (
          <div className="adp-edit-bar">
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              href={`/article/${params.seriesCode}/${params.articleId}/edit`}
            >
              編集
            </Button>
          </div>
        )}

        <div className="adp-layout">
          {/* ============ 左サイドバー（目次） ============ */}
          <aside className="adp-sidebar">
            <nav className="adp-toc-card" aria-label="目次">
              <div className="adp-toc-head">目次 ─ INDEX</div>
              {tocEntries.length === 0 ? (
                <div className="adp-toc-empty">（見出しがありません）</div>
              ) : (
                <ul className="adp-toc-list">
                  {tocEntries.map((t) => (
                    <li key={t.id}>
                      <a
                        href={`#${t.id}`}
                        onClick={(e) => onTocClick(e, t.id)}
                        className={`${t.level === 3 ? 'adp-toc-h3' : ''} ${
                          activeTocId === t.id ? 'is-active' : ''
                        }`}
                      >
                        {t.text}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </nav>

            {/* タグカード（tags があれば表示） */}
            {(article.tags || []).length > 0 && (
              <div className="adp-tag-card">
                <div className="adp-tag-head">このトピックについて</div>
                <ul className="adp-tag-list">
                  {article.tags.map((t) => (
                    <li key={t}>
                      <span className="adp-tag">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          {/* ============ 本文カード ============ */}
          <article className="adp-article">
            {/* 記事タイトル（タグ + タイトル + サブタイトル + メタ） */}
            <header className="adp-article-title">
              <div className="adp-episode-tags">
                {/* 下書きバッジ: 管理者(role 9)にのみ表示。一覧の下書き表示と整合させ、
                    公開前の記事を開いたときに状態がひと目で分かるようにする */}
                {userRole === 9 && article.status === 'draft' && (
                  <span className="adp-ep-tag adp-ep-tag--draft">下書き</span>
                )}
                {article.edition_name && (
                  <span className="adp-ep-tag">{article.edition_name}</span>
                )}
                {article.category_name && (
                  <span className="adp-ep-tag adp-cat-tag">
                    {article.category_name}
                  </span>
                )}
              </div>
              <h1>
                <span className="adp-title-main">{article.title}</span>
                {article.sub_title && (
                  <span className="adp-title-sub">{article.sub_title}</span>
                )}
              </h1>
              <div className="adp-article-meta">
                {publishDateStr && (
                  <span className="adp-mi">{publishDateStr}</span>
                )}
                {article.reading_time && (
                  <span className="adp-mi">{article.reading_time}</span>
                )}
                {(article.target || []).length > 0 && (
                  <span className="adp-mi">
                    {(article.target || []).join('・')}
                  </span>
                )}
                {currentEpisode != null && (
                  <span className="adp-mi adp-ep-no">
                    EP {String(currentEpisode).padStart(2, '0')}
                  </span>
                )}
              </div>
            </header>

            {/* ヒーローサムネイル（thumbnail があれば表示） */}
            {article.thumbnail && (
              <div className="adp-article-hero">
                <img src={article.thumbnail} alt={article.title} />
              </div>
            )}

            {/* 本文 */}
            <div className="adp-body">
              {article.summary && (
                <div className="adp-article-lead">{article.summary}</div>
              )}

              <div className="adp-article-body" ref={bodyRef}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={componentsMap}
                >
                  {article.content_md || ''}
                </ReactMarkdown>
              </div>

              {/* 著者プロフィール */}
              <footer className="adp-editor-card">
                <div className="adp-editor-card-header">本シリーズの著者</div>
                <div className="adp-editor-card-body">
                  {authorPhoto ? (
                    <img
                      className="adp-editor-card-icon"
                      src={authorPhoto}
                      alt={authorName}
                    />
                  ) : (
                    <div className="adp-editor-card-icon adp-editor-card-placeholder">
                      {authorInitial}
                    </div>
                  )}
                  <div className="adp-editor-card-content">
                    <div className="adp-editor-card-name">{authorName}</div>
                    {authorTitle && (
                      <div className="adp-editor-card-title">{authorTitle}</div>
                    )}
                    {authorBio && (
                      <div className="adp-editor-card-bio">
                        <p>{authorBio}</p>
                      </div>
                    )}
                  </div>
                </div>
              </footer>

              {/* 前後ナビゲーション */}
              <nav
                className={`adp-article-nav ${
                  !prev ? 'adp-article-nav--end-only' : ''
                }`}
                aria-label="前後の記事"
              >
                {prev && (
                  <a
                    href={`/article/${prev.series_code || params.seriesCode}/${
                      prev._id
                    }`}
                    className="adp-article-nav-item adp-article-nav-prev"
                  >
                    <span className="adp-article-nav-label">前の記事</span>
                    <div className="adp-article-nav-meta">
                      EP {String(prev.episode_number).padStart(2, '0')}
                    </div>
                    <div className="adp-article-nav-title">{prev.title}</div>
                  </a>
                )}
                {next ? (
                  <a
                    href={`/article/${next.series_code || params.seriesCode}/${
                      next._id
                    }`}
                    className="adp-article-nav-item adp-article-nav-next"
                  >
                    <span className="adp-article-nav-label">次の記事</span>
                    <div className="adp-article-nav-meta">
                      EP {String(next.episode_number).padStart(2, '0')}
                    </div>
                    <div className="adp-article-nav-title">{next.title}</div>
                  </a>
                ) : (
                  <span className="adp-article-nav-item adp-article-nav-next adp-article-nav-disabled">
                    <span className="adp-article-nav-label">次の記事</span>
                    <div className="adp-article-nav-title">
                      （最後の記事です）
                    </div>
                  </span>
                )}
              </nav>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

export default ArticleDetailPage;
